import { getEnv, getRoot } from 'mobx-easy';
import {
	DataOptions,
	GetAllDriversOptions,
	KeysetPaginationRequest,
	MappedStorableItem,
	EventCallbackOptions,
	UserGroupValues
} from '@bringg/dashboard-sdk';
import { reaction } from 'mobx';
import { User } from '@bringg/types';

import { RootEnv } from '../root-env';
import { RootStore } from '../root-store-types';
import DriversStore from './drivers-store';

export default class DriversStoreAdapter {
	private readonly api = getEnv<RootEnv>().dashboardSdk.sdk.v2.users;
	private readonly localStore: DriversStore;

	constructor() {
		this.localStore = new DriversStore();
	}

	public afterRootInit() {
		reaction(
			() => getRoot<RootStore>().views.authStore.isLoggedIn,
			isLoggedIn => {
				if (isLoggedIn) {
					this.localStore.afterUserLoggedIn();
				} else {
					this.localStore.afterUserLoggedOut();
				}
			}
		);
	}

	public loadAll = async (request: KeysetPaginationRequest<DataOptions> = {}) => {
		return this.api.loadAll(request);
	};

	public fetchAllDrivers = async (options: KeysetPaginationRequest<GetAllDriversOptions> = {}) => {
		return this.api.getAllDrivers(options);
	};

	public loadAdminsAndDispatchers = async (request: KeysetPaginationRequest<null> = {}) => {
		return this.api.getAllAdminsAndDispatchers(request);
	};

	public get(id: number) {
		return this.api.get(id);
	}

	public load = async (id: number, options?: DataOptions) => {
		return this.api.load(id, options);
	};

	public loadMany = async (ids: number[], options?: DataOptions) => {
		return this.api.loadMany(ids, options);
	};

	public loadByTeam = async (teamId: number) => {
		return this.api.loadAllByTeam(teamId);
	};

	public getByTeam = (teamId: number) => {
		return this.api.getTeamGroup(teamId);
	};

	public allByTeamAndCompany = (teamId: number, companyId: number) => {
		const driversByTeam = this.getByTeam(teamId);
		return driversByTeam?.filter(driver => driver.company_id === companyId);
	};

	get all() {
		return this.api.getAll();
	}

	get onlineDrivers() {
		return this.api.getGroup(UserGroupValues.OnlineDrivers);
	}

	get onShiftDrivers() {
		return this.api.getGroup(UserGroupValues.OnShiftDrivers);
	}

	get admins() {
		return this.api.getGroup(UserGroupValues.Admins);
	}

	get allAdminsAndDispatchers() {
		return [...this.api.getGroup(UserGroupValues.Admins), ...this.api.getGroup(UserGroupValues.Dispatchers)];
	}

	public create = async (user: Partial<User>) => {
		return this.api.create(user);
	};

	public delete = async (id: number) => {
		return this.api.delete(id);
	};

	public batchDelete = async (ids: number[]) => {
		return this.api.deleteMany(ids);
	};

	public onUpdate(callback: (mappedStorableItem: MappedStorableItem<User>) => void, options?: EventCallbackOptions) {
		return this.api.onUpdate(callback, options);
	}

	public onCreate(callback: (mappedStorableItem: MappedStorableItem<User>) => void) {
		return this.api.onCreate(callback);
	}
}
